/**
 * Subscription Settings Page
 * Affiche le plan actuel et permet de gérer l'abonnement via Stripe Billing Portal
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Crown, ExternalLink, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { useSubscriptions } from '@/hooks/useSubscriptions';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

interface SubscriptionInfo {
  planCode: string;
  planName: string;
  status: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
}

export const SubscriptionSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCurrentSubscription, openBillingPortal, loading } = useSubscriptions();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [portalError, setPortalError] = useState<string | null>(null);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoadingData(true);
      const sub = await getCurrentSubscription();
      if (!sub) {
        setSubscription(null);
        return;
      }
      const data = sub as Record<string, any>;
      const planCode = data.SubscriptionPlan?.code || data.plan?.code || '';
      const planName = data.SubscriptionPlan?.name || data.plan?.name || planCode;
      setSubscription({
        planCode,
        planName,
        status: data.status || 'active',
        currentPeriodEnd: data.currentPeriodEnd,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
      });
    } catch {
      setSubscription(null);
    } finally {
      setLoadingData(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      active: t('subscriptions.settings.statusActive'),
      trialing: t('subscriptions.settings.statusTrial'),
      canceled: t('subscriptions.settings.statusCanceled'),
      past_due: t('subscriptions.settings.statusPastDue'),
    };
    return map[status] || status;
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      active: 'bg-emerald-100 text-emerald-700',
      trialing: 'bg-blue-100 text-blue-700',
      canceled: 'bg-red-100 text-red-700',
      past_due: 'bg-amber-100 text-amber-700',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // Pas d'abonnement
  if (!subscription || subscription.planCode === 'FREE') {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
            <Crown className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('subscriptions.settings.noPlan')}
          </h3>
          <p className="text-gray-500 mb-6">
            {t('subscriptions.settings.noPlanDescription')}
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-colors"
          >
            {t('subscriptions.settings.choosePlan')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-teal-100 rounded-xl">
          <Crown className="w-5 h-5 text-teal-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          {t('subscriptions.settings.title')}
        </h2>
      </div>

      {/* Current Plan Card */}
      <div className={cardClasses}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-3">
            {/* Plan */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('subscriptions.settings.currentPlanLabel')}
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {subscription.planName}
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(subscription.status)}`}>
                {getStatusLabel(subscription.status)}
              </span>
              {subscription.cancelAtPeriodEnd && (
                <span className="text-xs text-amber-600 font-medium">
                  {t('subscriptions.settings.cancelAtPeriodEnd')}
                </span>
              )}
            </div>

            {/* Next billing */}
            {subscription.currentPeriodEnd && (
              <p className="text-sm text-gray-500">
                {t('subscriptions.settings.nextBillingLabel')} :{' '}
                <span className="font-medium text-gray-700">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center justify-center gap-2 px-5 py-2.5 border border-teal-600 text-teal-600 hover:bg-teal-50 rounded-xl font-medium text-sm transition-colors"
            >
              {t('subscriptions.settings.changePlan')}
            </button>
          </div>
        </div>
      </div>

      {/* Stripe Portal Card */}
      <div className={cardClasses}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900">
              {t('subscriptions.settings.manageOnStripe')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('subscriptions.settings.manageDescription')}
            </p>
          </div>
          <button
            onClick={async () => {
              setPortalError(null);
              try {
                await openBillingPortal();
              } catch (err: any) {
                const msg = err.message || '';
                if (msg.includes('not configured')) {
                  setPortalError(t('subscriptions.errors.stripeNotConfigured'));
                } else if (msg.includes('No Stripe customer') || msg.includes('no customer') || msg.includes('Aucun client Stripe') || msg.includes('souscrire')) {
                  setPortalError(t('subscriptions.errors.noStripeCustomer'));
                } else if (msg.includes('Billing Portal')) {
                  setPortalError(t('subscriptions.errors.billingPortalFailed'));
                } else {
                  setPortalError(t('subscriptions.errors.portalGeneric'));
                }
              }
            }}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-colors whitespace-nowrap disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4" />
            )}
            {t('subscriptions.billingPortal.button')}
          </button>
        </div>
        {portalError && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{portalError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSettingsPage;
